import React from 'react';
import CaptureSourceAttribution from './CaptureSourceAttribution';
import {
  Box,
  ButtonBase,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';
import LogFormShell from '../../components/UI/LogFormShell';

const MAX_VISIBLE_CAPTURES = 2;
const MAX_HISTORY_VISIBLE_CAPTURES = 5;

const isAttentionCapture = (group = {}) => (
  Boolean(group.isProcessing || group.hasFailed || group.needsReview)
);

export const getRecentActivityVisibleGroups = (groups = []) => groups.slice(0, MAX_VISIBLE_CAPTURES);

export const getRecentActivityHistoryGroups = (groups = []) => {
  const primaryGroups = groups.slice(0, MAX_HISTORY_VISIBLE_CAPTURES);
  const includedKeys = new Set(primaryGroups.map((group) => group.key));
  const attentionGroups = groups.filter((group) => (
    isAttentionCapture(group) && !includedKeys.has(group.key)
  ));
  return [...primaryGroups, ...attentionGroups];
};

const isSameCalendarDay = (left, right) => (
  left.getFullYear() === right.getFullYear()
  && left.getMonth() === right.getMonth()
  && left.getDate() === right.getDate()
);

export const formatCaptureTimestamp = (millis, now = new Date()) => {
  if (!millis) return '';

  const capturedAt = new Date(millis);
  if (Number.isNaN(capturedAt.getTime())) return '';

  const timeLabel = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(capturedAt);
  if (isSameCalendarDay(capturedAt, now)) return `Today, ${timeLabel}`;

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameCalendarDay(capturedAt, yesterday)) return `Yesterday, ${timeLabel}`;

  const dateLabel = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    ...(capturedAt.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  }).format(capturedAt);
  return `${dateLabel}, ${timeLabel}`;
};

const CaptureRow = ({ group, onOpen }) => (
  <ButtonBase
    data-testid={`compact-capture-${group.key}`}
    onClick={() => onOpen(group.key)}
    aria-label={
      group.isProcessing
        ? `Processing ${group.title}`
        : `${group.hasFailed ? 'Open retry options for' : 'Open capture details for'} ${group.title}`
    }
    sx={{
      width: '100%',
      px: { xs: 1.25, sm: 1.5 },
      py: 0.8,
      minHeight: 44,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      textAlign: 'left',
      transition: 'background-color 140ms ease',
      '&:hover': { bgcolor: alpha(colors.semantic.sageMist, 0.62) },
      '&:active': { bgcolor: alpha(colors.semantic.sage, 0.14) },
    }}
  >
    {group.isProcessing ? (
      <CircularProgress
        aria-label="Organizing capture"
        size={15}
        thickness={5}
        sx={{ color: colors.semantic.sage, flexShrink: 0 }}
      />
    ) : null}
    <Box sx={{ minWidth: 0, flex: 1 }}>
      {group.dateLabel ? (
        <Typography sx={{
          fontSize: '0.7rem',
          fontWeight: 500,
          color: alpha(colors.semantic.ink, 0.5),
        }}>
          {`Captured: ${group.dateLabel}`}
        </Typography>
      ) : null}
      <Typography noWrap sx={{
        mt: 0.1,
        fontSize: '0.8rem',
        fontWeight: group.needsReview ? 800 : 600,
        color: group.hasFailed ? '#B42318' : colors.semantic.ink,
      }}>
        {group.isProcessing
          ? 'Organizing your update…'
          : group.hasFailed ? "We couldn't organize this update" : group.title}
      </Typography>
      {!group.isProcessing && !group.hasFailed && group.resultLabel ? (
        <Typography noWrap sx={{
          mt: 0.1,
          fontSize: '0.72rem',
          fontWeight: group.resultTone === 'attention' ? 800 : group.resultTone === 'success' ? 700 : 500,
          color: group.resultTone === 'attention'
            ? colors.semantic.warning
            : group.resultTone === 'success'
              ? colors.semantic.success
              : alpha(colors.semantic.ink, 0.5),
          lineHeight: 1.3,
        }}
        data-testid={`capture-result-${group.key}`}
        data-tone={group.resultTone || 'secondary'}>
          {group.resultLabel}
        </Typography>
      ) : group.isProcessing ? (
        <>
          <Typography noWrap sx={{
            mt: 0.1,
            fontSize: '0.72rem',
            color: alpha(colors.semantic.ink, 0.58),
          }}>
            {group.title}
          </Typography>
          <Typography sx={{
            mt: 0.1,
            fontSize: '0.72rem',
            color: alpha(colors.semantic.ink, 0.58),
            lineHeight: 1.3,
          }}>
            You can keep using Lifelog while we finish.
          </Typography>
        </>
      ) : group.hasFailed ? (
        <Typography sx={{
          mt: 0.1,
          fontSize: '0.72rem',
          color: alpha(colors.semantic.ink, 0.58),
          lineHeight: 1.3,
        }}>
          Your original is saved. Open it to retry.
        </Typography>
      ) : null}
    </Box>
    {group.isProcessing ? (
      <Typography sx={{ flexShrink: 0, fontSize: '0.74rem', fontWeight: 800, color: alpha(colors.semantic.ink, 0.56) }}>
        Processing...
      </Typography>
    ) : (
      <ChevronRightRoundedIcon
        aria-hidden="true"
        sx={{ flexShrink: 0, fontSize: 20, color: alpha(colors.semantic.ink, 0.48) }}
      />
    )}
  </ButtonBase>
);

const CaptureHistoryItem = ({ group }) => (
  <Box>
    <Typography sx={{
      mb: 0.45,
      px: 0.25,
      fontSize: '0.72rem',
      fontWeight: group.needsReview ? 800 : 500,
      color: alpha(colors.semantic.ink, 0.55),
    }}>
      {`Captured: ${group.dateLabel} · ${group.title}`}
    </Typography>
    <CaptureSourceAttribution sources={group.sources || []} />
    {group.renderDetails()}
  </Box>
);

const RecentActivitySummaryPanel = ({
  groups = [],
  initialOpenCaptureDetailsKey = '',
  initialOpenCaptureRequestKey = '',
}) => {
  const openedInitialRequestRef = React.useRef('');
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [historyMode, setHistoryMode] = React.useState('capture');
  const [selectedKey, setSelectedKey] = React.useState('');
  const visibleGroups = getRecentActivityVisibleGroups(groups);
  const historyGroups = getRecentActivityHistoryGroups(groups);
  const isUpdating = groups.some((group) => group.isProcessing);
  const selectedGroups = selectedKey
    ? groups.filter((group) => group.key === selectedKey)
    : groups;

  const openHistory = (key = '', mode = 'capture') => {
    setSelectedKey(key);
    setHistoryMode(mode);
    setHistoryOpen(true);
  };

  React.useEffect(() => {
    if (
      !initialOpenCaptureDetailsKey
      || openedInitialRequestRef.current === (initialOpenCaptureRequestKey || initialOpenCaptureDetailsKey)
      || !groups.some((group) => group.key === initialOpenCaptureDetailsKey)
    ) return;
    openedInitialRequestRef.current = initialOpenCaptureRequestKey || initialOpenCaptureDetailsKey;
    setSelectedKey(initialOpenCaptureDetailsKey);
    setHistoryMode('capture');
    setHistoryOpen(true);
  }, [groups, initialOpenCaptureDetailsKey, initialOpenCaptureRequestKey]);

  return (
    <>
      <Paper
        elevation={0}
        aria-label="Recent activity"
        sx={{
          borderRadius: '16px',
          border: `1px solid ${alpha(colors.semantic.line, 0.9)}`,
          bgcolor: '#FFFFFF',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: { xs: 1.25, sm: 1.5 }, py: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 900, color: colors.semantic.ink }}>
              Recent Activity
            </Typography>
            <Button href="/sources" size="small">View all captures</Button>
            {isUpdating ? (
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: alpha(colors.semantic.ink, 0.56) }}>
                Updating...
              </Typography>
            ) : null}
          </Stack>
          {!visibleGroups.length ? (
            <Typography sx={{ mt: 0.35, fontSize: '0.78rem', color: alpha(colors.semantic.ink, 0.58) }}>
              No recent activity yet.
            </Typography>
          ) : null}
        </Box>

        {visibleGroups.length ? <Divider /> : null}
        <Stack divider={<Divider flexItem />}>
          {visibleGroups.map((group) => (
            <CaptureRow key={group.key} group={group} onOpen={openHistory} />
          ))}
        </Stack>
      </Paper>

      <LogFormShell
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={historyMode === 'saved'
          ? 'Saved care details'
          : selectedKey ? 'Organized by Lifelog' : 'Capture history'}
        subtitle={selectedKey
          ? 'What Lifelog understood and organized from this capture'
          : historyMode === 'saved'
            ? 'What Lifelog saved from your recent notes'
            : `${historyGroups.length} recent capture${historyGroups.length === 1 ? '' : 's'}`}
        forceDrawer
        compactTitle
        surfaceSx={{ height: '88dvh', maxHeight: '88dvh' }}
        bodySx={{ px: { xs: 1.25, sm: 3 }, pt: 1.25, pb: 3 }}
      >
        <Stack spacing={1}>
          {historyOpen ? selectedGroups.map((group) => (
            <CaptureHistoryItem key={group.key} group={group} />
          )) : null}
        </Stack>
      </LogFormShell>
    </>
  );
};

export default RecentActivitySummaryPanel;
